import { platformFetch } from '@/lib/api';

export interface MfaResponse {
  accessToken: string;
  expiresIn: number;
}

/** MFA deshabilitado temporalmente: el login con contraseña devuelve la sesión completa. */
export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
}

export interface MfaSetupResponse {
  otpauthUri: string;
  /** Secreto Base32 para ingreso manual en el autenticador. */
  secret: string;
}

export interface MfaConfirmResponse {
  recoveryCodes: string[];
  /** Solo en el primer enrolamiento: abre la sesión completa. */
  accessToken?: string;
  expiresIn?: number;
}

export type SecondFactor = { code: string } | { recoveryCode: string };

// En todos estos endpoints un 401 es "credencial o código inválido", no sesión expirada (OS-1710).
const NO_REDIRECT = { redirectOnUnauthorized: false };

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

export async function platformLogin(email: string, password: string): Promise<LoginResponse> {
  return platformFetch<LoginResponse>(
    '/platform/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) },
    NO_REDIRECT,
  );
}

export async function platformVerifyMfa(
  factor: SecondFactor,
  partialToken: string,
): Promise<MfaResponse> {
  return platformFetch<MfaResponse>(
    '/platform/auth/verify-mfa',
    { method: 'POST', headers: authHeaders(partialToken), body: JSON.stringify(factor) },
    NO_REDIRECT,
  );
}

/** Inicia el enrolamiento (token de enrolamiento) o la rotación (sesión + código vigente). */
export async function platformSetupMfa(
  token: string,
  currentCode?: string,
): Promise<MfaSetupResponse> {
  return platformFetch<MfaSetupResponse>(
    '/platform/auth/setup-mfa',
    {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(currentCode ? { code: currentCode } : {}),
    },
    NO_REDIRECT,
  );
}

export async function platformConfirmMfa(token: string, code: string): Promise<MfaConfirmResponse> {
  return platformFetch<MfaConfirmResponse>(
    '/platform/auth/confirm-mfa',
    { method: 'POST', headers: authHeaders(token), body: JSON.stringify({ code }) },
    NO_REDIRECT,
  );
}

/** OS-1717 — respuesta de aceptar una invitación: abre la sesión completa (MFA deshabilitado). */
export interface AcceptInvitationResponse extends LoginResponse {
  email: string;
}

export async function platformAcceptInvitation(
  token: string,
  password: string,
): Promise<AcceptInvitationResponse> {
  return platformFetch<AcceptInvitationResponse>(
    '/platform/auth/accept-invitation',
    { method: 'POST', body: JSON.stringify({ token, password }) },
    NO_REDIRECT,
  );
}
